import type { FC, RefObject } from "react";
import { useRef, useState } from "react";

import type { AccountDropdownSettings } from "@ui/eva-components/MobileFirstHeader/Header";
import { useDeviceBreakpoint } from "@ui/hooks/useDeviceBreakpoint";
import { useOutsideClick } from "@ui/hooks/useOutsideClick";
import { useTranslations } from "@ui/providers/useTranslations";
import classNames from "classnames";

import { useConfig } from "@jti/app-context/useConfig";
import { Loader } from "@jti/core-components/components/Loader";
import { useDirection } from "@jti/fe-common";
import { useMessageBus } from "@jti/fe-message-bus";
import type { IUser } from "@jti/rest-api-types";

import type { HeaderToolsAvatarButtonProps, HeaderToolsAvatarVariant } from "../../../HeaderToolsAvatar";
import { HeaderToolsAvatar } from "../../../HeaderToolsAvatar";
import { HeaderToolsItem } from "../../../HeaderToolsItem";
import { USER_DIALOG_SELECTOR, UserDialog } from "../../UserDialog";
import { getFullUserName } from "../../utils/getFullUserName";

import styles from "./UserIndicationButton.module.scss";

// The dialog is rendered through a portal on mobile, so it is not a DOM descendant
// of the trigger wrapper and has to be excluded from the outside click detection.
const OUTSIDE_CLICK_OPTIONS = { excludeSelectors: [USER_DIALOG_SELECTOR] };

export type UserIndicationButtonProps = {
  accountDropdownSettings?: AccountDropdownSettings;
  accountLink: string;
  accountLabel?: string;
  accountLoadingLabel?: string;
  isEnabledUserIndication?: boolean;
  isEnabledUserNameForIndication?: boolean;
  isAuthPage?: boolean;
  onLogOut: () => void;
  userDialogPortalRef?: RefObject<HTMLElement | null>;
};

export type UserIndicationAdditionalProps = { isUserLoggedIn: boolean; user: IUser | null; isUserPending: boolean };

export const UserIndicationButton: FC<UserIndicationButtonProps & UserIndicationAdditionalProps> = ({
  accountDropdownSettings,
  accountLink,
  accountLabel,
  accountLoadingLabel,
  isUserLoggedIn,
  onLogOut,
  isEnabledUserIndication,
  isEnabledUserNameForIndication,
  isUserPending,
  user,
  userDialogPortalRef,
}) => {
  const messageBus = useMessageBus();
  const { __ } = useTranslations();
  const { isSingleNameField, isReversedNameOrder } = useConfig();

  const userName = getFullUserName(__, user, isSingleNameField, isReversedNameOrder);
  const [isUserDialogOpen, setIsUserDialogOpen] = useState(false);

  const dialogRef = useRef<HTMLDivElement>(null);

  const { breakpoint } = useDeviceBreakpoint();
  const userDialogPortalContainer = breakpoint === "mobile" ? (userDialogPortalRef?.current ?? null) : null;

  useOutsideClick(
    dialogRef,
    () => {
      setIsUserDialogOpen(false);
    },
    OUTSIDE_CLICK_OPTIONS
  );

  const direction = useDirection();
  const isRtl = direction === "rtl";

  const publishAuthNavigation = () =>
    messageBus.publish("user.auth.root.navigation", {
      payload: null,
    });

  if (isUserPending) {
    return (
      <HeaderToolsItem aria-busy="true" aria-label={accountLoadingLabel ?? accountLabel}>
        <Loader size={"sm"} aria-label={accountLoadingLabel ?? accountLabel} />
      </HeaderToolsItem>
    );
  }

  if (!isUserLoggedIn) {
    return (
      <HeaderToolsAvatar
        href={accountLink}
        onClick={publishAuthNavigation}
        aria-label={accountLabel}
        title={accountLabel}
        data-testid="headerLoginButton"
        variant="default"
      />
    );
  }

  const isNameIndicationEnabled = !!isEnabledUserIndication && !!isEnabledUserNameForIndication;

  // The name variant does not fit the mobile header, so initials are rendered instead.
  // Both avatars are always in the DOM and toggled by CSS to keep SSR markup stable.
  const avatarsToRender: { variant: HeaderToolsAvatarVariant; className?: string }[] = isNameIndicationEnabled
    ? [
        { variant: "initials", className: styles.avatarMobileOnly },
        { variant: "name", className: styles.avatarDesktopOnly },
      ]
    : [{ variant: isEnabledUserIndication ? "initials" : "default" }];

  const sharedAvatarProps: HeaderToolsAvatarButtonProps = {
    onClick: () => setIsUserDialogOpen((prev) => !prev),
    "aria-expanded": isUserDialogOpen,
    "aria-haspopup": "true",
    "aria-label": userName ?? undefined,
    initials: userName?.[0]?.toUpperCase(),
    name: userName ?? undefined,
  };

  return (
    <div
      ref={dialogRef}
      className={classNames({
        [styles.userButtonWithNameRtl]: isNameIndicationEnabled && isRtl,
      })}
    >
      {avatarsToRender.map(({ variant, className }) => (
        <HeaderToolsAvatar key={variant} {...sharedAvatarProps} variant={variant} className={className} />
      ))}
      {isUserDialogOpen && (
        <UserDialog
          setIsUserDialogOpen={setIsUserDialogOpen}
          accountDropdownSettings={accountDropdownSettings}
          userName={userName}
          onLogOut={onLogOut}
          isEnabledUserIndicationWithName={isNameIndicationEnabled}
          portalContainer={userDialogPortalContainer}
        />
      )}
    </div>
  );
};
